<template>
  <div class="comenzi-container">

    <div class="tabs-row">
      <div class="tab-fixed" :class="{ 'tab-fixed-active': selectedIndex === 0 }" @click="goToListTab">
        <span>Comenzi</span>
      </div>
      <DxTabs
        v-if="orderTabs.length > 0"
        class="tabs-scroll"
        :data-source="orderTabs"
        :selected-index="selectedIndex - 1"
        item-template="tabItem"
        :scrolling-enabled="true"
        :show-nav-buttons="true"
        @option-changed="onTabChanged"
      >
        <template #tabItem="{ data, index }">
          <div class="tab-label">
            <span>{{ data.text }}</span>
            <span class="tab-close" @click.stop="closeTab(index)">×</span>
          </div>
        </template>
      </DxTabs>
    </div>

    <!-- Tab 0: Lista comenzi -->
    <div v-if="selectedIndex === 0" class="tab-content">
      <div class="filters-bar">
        <FiltreComenzi @filtersChanged="updateFilters" />
      </div>
      <GridComenzi
        :filters="filters"
        @openOrder="openOrder"
        class="grid-fill"
      />
    </div>

    <!-- Tab-uri comenzi deschise (index 1+) -->
    <template v-for="(order, i) in openOrders" :key="order.orderId">
      <div v-if="selectedIndex === i + 1" class="tab-content">
        <GridComandaDetalii
          :ref="(el) => setGridRef(order.orderId, el)"
          :comandaId="order.orderId"
          :orderData="order.orderData"
          :is-new="order.isNew"
          :suppress-auto-save="suppressChildAutoSave"
          class="fill"
          @open-order="openOrder"
          @unsaved-changes-change="(has) => onUnsavedChange(order.orderId, has)"
        />
      </div>
    </template>

  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, nextTick } from 'vue';
import { useRoute, onBeforeRouteLeave } from 'vue-router';
import DxTabs from 'devextreme-vue/tabs';
import { custom } from 'devextreme/ui/dialog';
import FiltreComenzi from './FilterComenzi.vue';
import GridComenzi from './GridComenzi.vue';
import GridComandaDetalii from './GridComandaDetalii.vue';

interface Filter {
  category: number | null;
  start: string;
  end: string;
}

interface OrderData {
  Id: string | number;
  Cod: string;
  [key: string]: any;
}

interface OpenOrderTab {
  orderId: string;
  orderData: OrderData;
  isNew?: boolean;
}

const route = useRoute();

const filters = ref<Filter>({ category: null, start: '', end: '' });
const selectedIndex = ref(0);
const openOrders = ref<OpenOrderTab[]>([]);
const orderTabs = ref<{ text: string; orderId: string }[]>([]);

// Setat pe durata schimbărilor de tab pornite din cod (după ce am confirmat/salvat),
// ca să nu retrigger-uim propriile noastre actualizări de selectedIndex.
let suppressTabGuard = false;

// Setat cât timp confirmSwitchAway verifică/tratează comanda părăsită — transmis ca prop
// către GridComandaDetalii, ca auto-salvarea ei proprie (pe focusout) să nu intervină
// concurent peste dialogul Salvează/Renunță/Rămân.
const suppressChildAutoSave = ref(false);

// ── Tracking modificari nesalvate per tab ──
const unsavedOrderIds = ref<Set<string>>(new Set());
const gridRefs = ref<Map<string, any>>(new Map());

function setGridRef(orderId: string, el: any) {
  if (el) gridRefs.value.set(orderId, el);
  else gridRefs.value.delete(orderId);
}

function onUnsavedChange(orderId: string, has: boolean) {
  const next = new Set(unsavedOrderIds.value);
  has ? next.add(orderId) : next.delete(orderId);
  unsavedOrderIds.value = next;
}

function hasAnyUnsaved(): boolean {
  return unsavedOrderIds.value.size > 0;
}

async function confirmLeave(): Promise<boolean> {
  return custom({
    title: 'Modificări nesalvate',
    messageHtml: 'Aveți modificări nesalvate în comenzi deschise. Sigur doriți să continuați?',
    buttons: [
      { text: 'Rămân', stylingMode: 'outlined', onClick: () => false },
      { text: 'Părăsesc', type: 'danger', onClick: () => true },
    ],
  }).show();
}

onBeforeRouteLeave(async (_to, _from, next) => {
  if (!hasAnyUnsaved()) { next(); return; }
  const ok = await confirmLeave();
  ok ? next() : next(false);
});

function currentOpenOrderId(): string | null {
  if (selectedIndex.value === 0) return null;
  return openOrders.value[selectedIndex.value - 1]?.orderId ?? null;
}

// Verifică/salvează linia needitată a comenzii PĂRĂSITE, ÎNAINTE de a comuta efectiv
// tab-ul (deci înainte ca v-if să demonteze componenta ei — o dată demontată, datele
// nesalvate din grid se pierd ireversibil, deci nu are rost să verificăm după).
// Dialogul apare de fiecare dată când există o linie needitată — nu doar dacă salvarea
// automată eșuează — ca userul să decidă explicit, nu să se întâmple ceva pe tăcute.
async function confirmSwitchAway(): Promise<boolean> {
  const orderId = currentOpenOrderId();
  if (!orderId) return true;

  const gridComp = gridRefs.value.get(orderId);
  if (!gridComp?.hasPendingChanges?.()) return true;

  const cod = orderTabs.value.find(t => t.orderId === orderId)?.text ?? '';
  const choice: 'save' | 'discard' | 'stay' = await custom({
    title: 'Linie nesalvată',
    messageHtml: `<div style="padding:4px 0">Comanda <b>${cod}</b> are un produs pe linie care nu a fost salvat.<br><br>Ce vrei să faci?</div>`,
    buttons: [
      { text: 'Salvează', type: 'default', onClick: () => 'save' },
      { text: 'Renunță la linie', type: 'danger', stylingMode: 'outlined', onClick: () => 'discard' },
      { text: 'Rămân pe comandă', stylingMode: 'outlined', onClick: () => 'stay' },
    ],
  }).show();

  if (choice === 'stay') return false;

  if (choice === 'discard') {
    gridComp.discardUnsavedLine?.();
    return true;
  }

  // 'save' — încearcă efectiv salvarea; dacă eșuează (ex. produs lipsă), arată din nou
  // dialogul, ca userul să aleagă altceva (renunță sau rămâne să corecteze).
  const { ok } = await gridComp.trySaveBeforeLeave();
  if (ok) return true;
  return confirmSwitchAway();
}

let tabSwitchInProgress = false;

async function onTabChanged(e: any) {
  // DxTabs declanșează "option-changed" pentru mai multe opțiuni interne la o singură
  // selecție (nu doar "selectedIndex") — fără filtrul ăsta, handler-ul rula de mai multe
  // ori per click, arătând dialogul de confirmare de 2-3 ori.
  if (e.name !== 'selectedIndex') return;
  if (suppressTabGuard || tabSwitchInProgress) return;

  const internalIndex = e.component.option('selectedIndex');
  if (internalIndex == null || internalIndex < 0) return;
  const targetIndex = internalIndex + 1;
  if (targetIndex === selectedIndex.value) return;

  const previousInternalIndex = selectedIndex.value - 1;
  tabSwitchInProgress = true;
  suppressChildAutoSave.value = true;
  try {
    const ok = await confirmSwitchAway();

    if (!ok) {
      suppressTabGuard = true;
      e.component.option('selectedIndex', previousInternalIndex);
      await nextTick();
      suppressTabGuard = false;
      return;
    }

    selectedIndex.value = targetIndex;
  } finally {
    tabSwitchInProgress = false;
    suppressChildAutoSave.value = false;
  }
}

async function goToListTab() {
  if (selectedIndex.value === 0) return;
  suppressChildAutoSave.value = true;
  try {
    const ok = await confirmSwitchAway();
    if (!ok) return;
    selectedIndex.value = 0;
  } finally {
    suppressChildAutoSave.value = false;
  }
}

function updateFilters(newFilters: Filter) {
  filters.value = newFilters;
  selectedIndex.value = 0;
}

function openOrder(order: OrderData) {
  const comandaIdString = String(order.Id);

  const existingIndex = openOrders.value.findIndex(t => t.orderId === comandaIdString);
  if (existingIndex !== -1) {
    selectedIndex.value = existingIndex + 1;
    return;
  }

  openOrders.value.push({ orderId: comandaIdString, orderData: order, isNew: !!order._isNew });
  orderTabs.value = [...orderTabs.value, { text: order.Cod, orderId: comandaIdString }];
  selectedIndex.value = orderTabs.value.length;
}

async function closeTab(index: number) {
  const order = openOrders.value[index];
  if (order && unsavedOrderIds.value.has(order.orderId)) {
    const ok = await custom({
      title: 'Modificări nesalvate',
      messageHtml: `Comanda <b>${orderTabs.value[index]?.text ?? ''}</b> are modificări nesalvate. Închizi oricum?`,
      buttons: [
        { text: 'Rămân', stylingMode: 'outlined', onClick: () => false },
        { text: 'Închide fără salvare', type: 'danger', onClick: () => true },
      ],
    }).show();
    if (!ok) return;
  }

  if (order) {
    unsavedOrderIds.value.delete(order.orderId);
    gridRefs.value.delete(order.orderId);
  }

  const wasActiveTab = selectedIndex.value === index + 1;
  const activeOrderId = wasActiveTab ? null : openOrders.value[selectedIndex.value - 1]?.orderId;

  openOrders.value.splice(index, 1);
  orderTabs.value = orderTabs.value.filter((_, i) => i !== index);

  if (wasActiveTab) {
    // Închizi comanda pe care o vizualizai — te întorci mereu pe gridul de comenzi
    selectedIndex.value = 0;
  } else {
    // Închizi un tab din fundal — rămâi pe comanda curentă, doar recalculezi indexul
    const newIndex = activeOrderId
      ? openOrders.value.findIndex(o => o.orderId === activeOrderId) + 1
      : 0;
    selectedIndex.value = newIndex;
  }
}

onMounted(() => {
  const comandaIdFromRoute = route.params.comandaId as string;
  if (comandaIdFromRoute) {
    openOrder({
      Id: comandaIdFromRoute,
      Cod: comandaIdFromRoute,
      Nume: 'Încărcare...',
      DataComenzii: '',
      ValoareComanda: 0,
    });
  }
});
</script>

<style scoped>
.comenzi-container {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.tabs-row {
  display: flex;
  align-items: stretch;
  flex-shrink: 0;
}

.tab-fixed {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 16px;
  cursor: pointer;
  white-space: nowrap;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.6);
  border-bottom: 2px solid transparent;
  transition: color 0.15s, border-color 0.15s;
}

.tab-fixed:hover {
  color: rgba(0, 0, 0, 0.87);
}

.tab-fixed-active {
  color: #ff2c21;
  border-bottom-color: #ff2c21;
  font-weight: 600;
}

.tabs-scroll {
  flex: 1;
  min-width: 0;
}

.tab-content {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.filters-bar {
  flex-shrink: 0;
  padding: 6px 8px 0;
}

.grid-fill {
  flex: 1;
  min-height: 0;
}

.fill {
  flex: 1;
  min-height: 0;
  width: 100%;
}

.tab-label {
  display: flex;
  align-items: center;
  gap: 6px;
}

.tab-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  font-size: 14px;
  line-height: 1;
  color: #94a3b8;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.tab-close:hover {
  background: #fee2e2;
  color: #ef4444;
}
</style>
